import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { usePrefabStore } from '../../stores/usePrefabStore';
import { PREFAB_CATEGORY_LABELS } from '../../types/prefab';
import type { PrefabCategory } from '../../types/prefab';
import s from './Pages.module.css';

const ALL_CATEGORIES = Object.entries(PREFAB_CATEGORY_LABELS) as [PrefabCategory, string][];

export function PrefabsPage() {
  const navigate = useNavigate();
  const searchQuery = usePrefabStore(p => p.searchQuery);
  const setSearchQuery = usePrefabStore(p => p.setSearchQuery);
  const selectedCategory = usePrefabStore(p => p.selectedCategory);
  const setSelectedCategory = usePrefabStore(p => p.setSelectedCategory);
  const getFiltered = usePrefabStore(p => p.getFilteredCatalog);
  const fetchCatalog = usePrefabStore(p => p.fetchCatalog);
  const loading = usePrefabStore(p => p.loading);
  const error = usePrefabStore(p => p.error);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const filtered = getFiltered();

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Prefabs</h1>
        <p className={s.pageSubtitle}>
          {filtered.length} verified no-code modules. Configure, deploy, and integrate without writing any code.
        </p>
      </div>

      {/* Search + filters */}
      <div className={s.searchBar}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 10, color: 'var(--color-text-tertiary)' }} />
          <input
            className={s.searchInput}
            style={{ paddingLeft: 36 }}
            placeholder="Search prefabs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--spacing-xl)' }}>
        <button
          className={`${s.filterChip} ${!selectedCategory ? s.filterActive : ''}`}
          onClick={() => setSelectedCategory(null)}
        >
          All
        </button>
        {ALL_CATEGORIES.map(([key, label]) => (
          <button
            key={key}
            className={`${s.filterChip} ${selectedCategory === key ? s.filterActive : ''}`}
            onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className={s.emptyState}>
          <Loader2 size={32} className={s.spinner} />
          <div className={s.emptyTitle}>Loading prefab catalog...</div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className={s.emptyState}>
          <div className={s.emptyTitle}>Failed to load prefabs</div>
          <div className={s.emptyDesc}>{error}</div>
          <button className={s.btnPrimary} onClick={() => { usePrefabStore.setState({ catalog: [], error: null }); fetchCatalog(); }}>
            Retry
          </button>
        </div>
      )}

      {/* Results */}
      {!loading && !error && filtered.length === 0 && (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><Search size={40} /></div>
          <div className={s.emptyTitle}>No prefabs found</div>
          <div className={s.emptyDesc}>Try adjusting your search or category filter.</div>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className={s.cardGrid}>
          {filtered.map(prefab => (
            <button
              key={prefab.id}
              className={s.card}
              onClick={() => navigate(`/prefabs/${prefab.id}`)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span className={s.cardTitle} style={{ marginBottom: 0 }}>{prefab.name}</span>
                {prefab.microSafe && (
                  <span className={s.verifiedBadge}>
                    <span style={{ fontSize: 10 }}>uSafe</span>
                  </span>
                )}
              </div>
              <div className={s.cardDesc}>{prefab.description}</div>
              <div className={s.cardMeta}>
                <span className={`${s.badge} ${s.badgeInfo}`}>
                  {PREFAB_CATEGORY_LABELS[prefab.category]}
                </span>
                <span className={`${s.badge} ${s.badgeNeutral}`}>
                  {prefab.kind}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  $1.00 registration
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                  v{prefab.version}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
